import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Church, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/lib/i18n';

export default function LoginPage({ mode = 'member' }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const isStaff = mode === 'staff';

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '', role: 'member'
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarInputRef = useRef(null);

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      // Role-based redirect
      const portals = { super_admin: '/dashboard', admin: '/admin-portal', pastor: '/pastor-portal', leader: '/leader-portal', member: '/my-portal' };
      navigate(portals[userData?.role] || '/my-portal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm);
      // Upload avatar if selected
      if (avatarFile) {
        try { await authAPI.uploadAvatar(avatarFile); } catch {}
      }
      toast.success('Account created! Welcome to NaziriteOS');
      navigate('/my-portal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - imagery */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1583472032780-5654e59e060a?w=1200&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <Church className="w-12 h-12 mb-4 opacity-80" />
          <h2 className="font-display text-4xl font-bold tracking-tight mb-3">NaziriteOS</h2>
          <p className="text-lg text-white/80 max-w-md leading-relaxed">
            Complete church management system. Manage members, track attendance, handle tithes & offerings, and grow your congregation.
          </p>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-4 lg:hidden">
            <Church className="w-8 h-8 text-primary" />
            <h1 className="font-display text-2xl font-bold">NaziriteOS</h1>
          </div>

          {/* Mode indicator */}
          <div className="mb-6 p-3 rounded-lg bg-muted text-center">
            <p className="text-sm font-medium text-foreground">
              {isStaff ? 'Staff & Admin Portal' : 'Member Portal'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isStaff ? (
                <>Need member access? <button onClick={() => navigate('/login')} className="text-primary font-medium hover:underline">Member Login</button></>
              ) : (
                <>Church staff? <button onClick={() => navigate('/staff-login')} className="text-primary font-medium hover:underline" data-testid="staff-portal-link">Staff Portal</button></>
              )}
            </p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            {!isStaff ? (
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t('signIn')}</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">{t('createAccount')}</TabsTrigger>
              </TabsList>
            ) : (
              <TabsList className="grid w-full grid-cols-1 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t('signIn')}</TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle className="font-display">{isStaff ? 'Staff Login' : 'Welcome Back'}</CardTitle>
                  <CardDescription>{isStaff ? 'Sign in with your staff credentials' : 'Sign in to your church account'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input id="login-email" type="email" placeholder="your@email.com" required
                        data-testid="login-email-input"
                        value={loginForm.email}
                        onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input id="login-password" type="password" placeholder="Enter password" required
                        data-testid="login-password-input"
                        value={loginForm.password}
                        onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-form-submit-button">
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle className="font-display">Create Account</CardTitle>
                  <CardDescription>Join your church community</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    {/* Profile Picture */}
                    <div className="flex justify-center">
                      <div className="relative cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
                        <div className="w-20 h-20 rounded-full bg-muted border-2 border-dashed border-border flex items-center justify-center overflow-hidden hover:border-primary transition-colors">
                          {avatarPreview ? (
                            <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            <Camera className="w-6 h-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Camera className="w-3 h-3 text-primary-foreground" />
                        </div>
                        <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} data-testid="avatar-upload-input" />
                      </div>
                    </div>
                    <p className="text-xs text-center text-muted-foreground -mt-2">Upload profile picture</p>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>First Name</Label>
                        <Input placeholder="First name" required data-testid="register-first-name-input"
                          value={registerForm.first_name}
                          onChange={e => setRegisterForm(f => ({ ...f, first_name: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name</Label>
                        <Input placeholder="Last name" required data-testid="register-last-name-input"
                          value={registerForm.last_name}
                          onChange={e => setRegisterForm(f => ({ ...f, last_name: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" placeholder="your@email.com" required data-testid="register-email-input"
                        value={registerForm.email}
                        onChange={e => setRegisterForm(f => ({ ...f, email: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input placeholder="+1 (555) 000-0000" data-testid="register-phone-input"
                        value={registerForm.phone}
                        onChange={e => setRegisterForm(f => ({ ...f, phone: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input type="password" placeholder="Create password" required data-testid="register-password-input"
                        value={registerForm.password}
                        onChange={e => setRegisterForm(f => ({ ...f, password: e.target.value }))}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="register-form-submit-button">
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="text-center mt-6">
            <button onClick={() => navigate('/')} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-website-link">
              &larr; Back to website
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
